import React, { useCallback, useState } from 'react';
import { IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { GroupTypeDetails } from 'graphql/types';
import { useMessage, useMessages } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { SimpleDropDown } from './SimpleDropDown';
import { GroupSelectClassification } from './GroupSelectClassification';
import { EnabledDisabled } from './constant';
import { SimpleRemoveDialogBox } from './SimpleRemoveDialogBox';

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    borderRadius: theme.spacing(0, 1.25, 0, 0),
    alignContent: 'flex-start',
    marginRight: ({
      isTiAutoAcceptUserSettingsEnabled
    }: {
      isTiAutoAcceptUserSettingsEnabled: boolean;
    }) =>
      isTiAutoAcceptUserSettingsEnabled ? theme.spacing(1) : theme.spacing(10)
  },
  dropDown: {
    marginRight: theme.spacing(1)
  },
  secondaryAction: {
    display: 'flex',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    width: '75%'
  },
  text: {
    display: 'flex',
    alignItems: 'start',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2)
  },

  removeButton: {
    alignSelf: 'center'
  }
}));

const alertMessage = [
  'settings.removeDialog.title',
  'settings.removeDialog.Remove',
  'settings.removeDialog.Cancel',
  'settings.removeDialog.dialogText1',
  'settings.removeDialog.dialogText2'
];

type GroupSelectionRowProps = {
  options: {
    key: EnabledDisabled;
    value: string;
  }[];
  enabledGroupTypes: GroupTypeDetails[] | undefined | null;
  onRemove: (rowIndex: number) => void;
  rowIndex: number;
  rowID: string;
  field: Record<string, any>;
};
export const GroupSelectionRow: React.FC<GroupSelectionRowProps> = ({
  options,
  enabledGroupTypes,
  onRemove,
  rowIndex,
  rowID,
  field
}: GroupSelectionRowProps) => {
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const classes = useStyles({ isTiAutoAcceptUserSettingsEnabled });
  const alertDialogBoxMessages = useMessages(alertMessage);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const handleDialogOpen = useCallback(() => {
    setRemoveDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => {
    setRemoveDialogOpen(false);
  }, []);
  const handleRemove = useCallback(() => {
    onRemove(rowIndex);
  }, [onRemove, rowIndex]);
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const removeButtonMsg = useMessage('settings.remove');

  return (
    <div
      className={classes.content}
      data-testid={`setting-content-layout-${rowIndex}`}
    >
      <div className={classes.dropDown}>
        <SimpleDropDown
          options={options}
          rowID={rowID}
          value={field.enableDisabled}
        ></SimpleDropDown>
      </div>
      <Typography
        display="inline"
        className={classes.text}
        variant={isTiAutoAcceptUserSettingsEnabled ? 'caption' : 'body2'}
      >
        <FormattedMessage id="settings.usersIn" />
      </Typography>

      <div className={classes.secondaryAction}>
        {enabledGroupTypes &&
          enabledGroupTypes.map(({ value }) => (
            <GroupSelectClassification
              groupType={value}
              key={value}
              rowID={rowID}
              defaultValues={field[value]}
            />
          ))}
      </div>
      <div className={classes.removeButton}>
        <IconButton
          color="secondary"
          onClick={handleDialogOpen}
          data-testid={`remove-button-${rowID}`}
          size="large"
          aria-label={isTiwScreenReaderEnabled ? removeButtonMsg : undefined}
        >
          <RemoveCircleOutlineOutlinedIcon />
        </IconButton>
        <SimpleRemoveDialogBox
          removeDialogOpen={removeDialogOpen}
          handleRemove={handleRemove}
          handleDialogClose={handleDialogClose}
          title={alertDialogBoxMessages['settings.removeDialog.title']}
          contentText={[
            alertDialogBoxMessages['settings.removeDialog.dialogText1'],
            alertDialogBoxMessages['settings.removeDialog.dialogText2']
          ]}
          textButton={alertDialogBoxMessages['settings.removeDialog.Remove']}
          containedButton={
            alertDialogBoxMessages['settings.removeDialog.Cancel']
          }
          icon={<RemoveCircleOutlineOutlinedIcon />}
        />
      </div>
    </div>
  );
};
